import {
  QueryClient,
  QueryClientProvider,
  type QueryClientConfig,
} from '@tanstack/react-query';
import { type PropsWithChildren, type FC } from 'react';

export type CreateQueryClientProviderMeta = {
  displayName?: string;
};

export type CreateQueryClientProviderProps = PropsWithChildren;

export type CreateQueryClientProviderComponent =
  FC<CreateQueryClientProviderProps>;

export const DEFAULT_NEO4J_QUERY_CLIENT_OPTIONS: QueryClientConfig = {
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
};

function mergeQueryClientOptions(
  base: QueryClientConfig,
  override?: QueryClientConfig,
): QueryClientConfig {
  if (!override) {
    return base;
  }

  return {
    ...base,
    ...override,
    defaultOptions: {
      ...base.defaultOptions,
      ...override.defaultOptions,
      queries: {
        ...base.defaultOptions?.queries,
        ...override.defaultOptions?.queries,
      },
      mutations: {
        ...base.defaultOptions?.mutations,
        ...override.defaultOptions?.mutations,
      },
    },
  };
}

/**
 * Returns a provider that shares one `QueryClient` for that factory-created
 * component. Remounting that provider reuses the same client (cache survives
 * unmount). Call `createQueryClientProvider` again for an isolated client.
 */
export function createQueryClientProvider(
  factoryOptions?: QueryClientConfig,
  meta?: CreateQueryClientProviderMeta,
): CreateQueryClientProviderComponent {
  const client = new QueryClient(
    mergeQueryClientOptions(DEFAULT_NEO4J_QUERY_CLIENT_OPTIONS, factoryOptions),
  );

  const Provider: CreateQueryClientProviderComponent = ({ children }) => {
    return (
      <QueryClientProvider client={client}>{children}</QueryClientProvider>
    );
  };

  Provider.displayName = meta?.displayName ?? 'Neo4jQueryClientProvider';

  return Provider;
}
